import { useNavigate } from "react-router";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/shared/status-badge";
import { EmptyState } from "@/components/shared/empty-state";
import { TableSkeleton } from "@/components/shared/loading-skeleton";
import { useGetApiAgents } from "@/lib/api/generated/agents/agents";
import { ROUTES } from "@/app/routes";
import { formatRelativeTime } from "@/lib/utils/formatting";
import { useDocumentTitle } from "@/lib/hooks/use-document-title";
import type { ControlPlaneAgentRecord } from "@/lib/types/control-plane-agents";

export function AgentListPage() {
  const { tenantId } = useTenantContext();
  const navigate = useNavigate();
  const { data: agents, isLoading } = useGetApiAgents<ControlPlaneAgentRecord[]>({
    query: { enabled: Boolean(tenantId) },
  });
  useDocumentTitle("Agents");

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold" style={{ color: "var(--text-strong)" }}>
          Agents
        </h2>
        <Button size="sm" onClick={() => navigate("/workspace/agents/new")}>
          <Plus size={14} />
          New Agent
        </Button>
      </div>

      {isLoading ? (
        <TableSkeleton rows={3} cols={4} />
      ) : !agents || agents.length === 0 ? (
        <EmptyState
          title="No agents"
          description="Create an agent to start authoring."
          action={{ label: "New Agent", onClick: () => navigate("/workspace/agents/new") }}
        />
      ) : (
        <div className="border border-border rounded-[var(--radius-md)] overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr style={{ background: "var(--bg-accent)" }}>
                <th className="text-left px-4 py-2 font-medium" style={{ color: "var(--muted-foreground)" }}>Name</th>
                <th className="text-left px-4 py-2 font-medium" style={{ color: "var(--muted-foreground)" }}>Slug</th>
                <th className="text-left px-4 py-2 font-medium" style={{ color: "var(--muted-foreground)" }}>Status</th>
                <th className="text-left px-4 py-2 font-medium" style={{ color: "var(--muted-foreground)" }}>Execution Class</th>
                <th className="text-right px-4 py-2 font-medium" style={{ color: "var(--muted-foreground)" }}>Updated</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((agent) => (
                <tr
                  key={agent.agentId}
                  className="border-t border-border cursor-pointer hover:bg-[var(--bg-hover)] transition-colors"
                  onClick={() => navigate(ROUTES.tenantAgent(tenantId!, agent.agentId))}
                >
                  <td className="px-4 py-2.5 font-medium" style={{ color: "var(--text-strong)" }}>
                    {agent.name}
                  </td>
                  <td className="px-4 py-2.5 font-mono" style={{ color: "var(--muted-foreground)" }}>
                    {agent.slug ?? "—"}
                  </td>
                  <td className="px-4 py-2.5">
                    <StatusBadge status={agent.status ?? "draft"} />
                  </td>
                  <td className="px-4 py-2.5" style={{ color: "var(--muted-foreground)" }}>
                    {agent.runtimePolicy.executionClass}
                  </td>
                  <td className="px-4 py-2.5 text-right" style={{ color: "var(--muted-foreground)" }}>
                    {formatRelativeTime(agent.updatedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
